import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bell, ExternalLink, Plane, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { prettyDate, gbp } from "@/lib/format";
import { api } from "@/lib/api";
import { travelApi, flagUrl, fieldFor } from "@/lib/travelApi";
import { ThingsToDo } from "@/pages/travel/TripPlanner";
import { yasminApi } from "@/lib/yasminApi";

function TripRow({ t }) {
  return (
    <Link to={`/travel/trips/${t.id}`} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-3 hover:bg-muted/60" data-testid={`trip-${t.id}`}>
      <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: fieldFor(t.city?.hue) }}>
        {flagUrl(t.city?.country_code) ? <img src={flagUrl(t.city.country_code)} alt="" className="h-5 w-8 rounded-[2px] object-cover" /> : <Plane size={20} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{t.name || t.city?.name}</span>
        <span className="block text-sm text-muted-foreground">{prettyDate(t.depart_date)}{t.return_date ? ` – ${prettyDate(t.return_date)}` : ""} · from {t.origin?.label || t.origin?.code}</span>
      </span>
      <span className="text-right text-sm">
        {t.has_flight ? <span className="font-mono-num font-semibold">{gbp(t.flight_price)}</span> : <span className="text-muted-foreground">No flight yet</span>}
        <span className="block text-xs text-muted-foreground">{t.items} booking{t.items === 1 ? "" : "s"}</span>
      </span>
    </Link>
  );
}

export function MyTrips() {
  usePageMeta("My trips", "Your saved trips on Fable +.");
  const [data, setData] = useState(undefined);
  const [plans, setPlans] = useState([]);
  const [watches, setWatches] = useState([]);
  useEffect(() => {
    travelApi.trips().then(setData).catch(() => setData(null));
    yasminApi.plans().then((r) => setPlans(r?.plans || [])).catch(() => {});
    yasminApi.watches().then((r) => setWatches((r?.watches || []).filter((w) => w.status === "watching"))).catch(() => {});
  }, []);
  const stopWatch = (id) => yasminApi.unwatch(id).then(() => setWatches((x) => x.filter((w) => w.id !== id))).catch((e) => toast.error(e.message || "Couldn't stop the alert."));
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="flex items-end justify-between gap-4">
        <h1 className="font-serif-display text-4xl font-bold">My trips</h1>
        <div className="flex gap-2">
          <Link to="/yasmin/chat"><Button variant="outline" className="rounded-full" data-testid="trips-ask-yasmin"><Sparkles size={16} className="mr-2" /> Ask Yasmin</Button></Link>
          <Link to="/travel/plan"><Button className="rounded-full"><Plane size={16} className="mr-2" /> Plan a trip</Button></Link>
        </div>
      </div>
      {plans.filter((p) => !p.past).length > 0 && (
        <section className="mt-8" data-testid="trips-yasmin-plans">
          <h2 className="text-sm font-semibold text-muted-foreground">Planned with Yasmin</h2>
          <div className="mt-3 space-y-3">
            {plans.filter((p) => !p.past).map((p) => (
              <Link key={p.id} to={"/travel/plans/" + p.id} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-3 hover:bg-muted/60" data-testid={"trips-plan-" + p.id}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: "linear-gradient(135deg,#8200EF,#FB0077)" }}><Sparkles size={18} /></span>
                <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{p.title}</span><span className="block text-sm text-muted-foreground">{p.start ? prettyDate(p.start) + (p.end && p.end !== p.start ? " – " + prettyDate(p.end) : "") : "No dates yet"}</span></span>
              </Link>
            ))}
          </div>
        </section>
      )}
      {watches.length > 0 && (
        <section className="mt-8" data-testid="trips-price-alerts">
          <h2 className="text-sm font-semibold text-muted-foreground">Flight price alerts · {watches.length}/5</h2>
          <div className="mt-3 space-y-2">
            {watches.map((w) => (
              <div key={w.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
                <Bell size={16} className="text-primary" />
                <Link to={"/travel/plans/" + w.plan_id} className="min-w-0 flex-1"><span className="block truncate font-semibold">{(w.origin_label || w.origin) + " → " + (w.destination_label || w.destination)}</span><span className="block text-xs text-muted-foreground">Now {gbp(w.last_price)} · checked daily</span></Link>
                <button type="button" onClick={() => stopWatch(w.id)} className="text-xs font-semibold text-muted-foreground hover:text-foreground">Stop</button>
              </div>
            ))}
          </div>
        </section>
      )}
      {data === undefined ? (
        <div className="mt-8 space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : data === null ? (
        <p className="mt-8 text-muted-foreground">Couldn't load your trips.</p>
      ) : (
        <>
          <h2 className="mt-8 text-sm font-semibold text-muted-foreground">Upcoming</h2>
          {data.upcoming?.length ? <div className="mt-3 space-y-3">{data.upcoming.map((t) => <TripRow key={t.id} t={t} />)}</div> : (
            <div className="mt-3 rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="font-semibold">No trips planned yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Pick a city and dates, see live fares, and keep your bookings for the trip in one place.</p>
            </div>
          )}
          {data.past?.length > 0 && (<><h2 className="mt-10 text-sm font-semibold text-muted-foreground">Past</h2><div className="mt-3 space-y-3">{data.past.map((t) => <TripRow key={t.id} t={t} />)}</div></>)}
        </>
      )}
    </div>
  );
}

export function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(undefined);
  useEffect(() => {
    travelApi.trip(id).then(setT).catch(() => setT(null));
  }, [id]);
  usePageMeta(t ? `${t.name || t.city?.name} trip` : "Trip", "A saved trip on Fable +.");
  const remove = async () => {
    if (!window.confirm("Delete this trip? Your bookings stay as they are.")) return;
    try {
      await api(`/travel/trips/${id}`, { method: "DELETE" });
      toast.success("Trip deleted");
      navigate("/travel/trips");
    } catch (e) {
      toast.error(e.message || "Couldn't delete the trip.");
    }
  };
  if (t === undefined) return <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6"><Skeleton className="h-64 rounded-3xl" /></div>;
  if (!t) return <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6"><p className="font-semibold">Trip not found</p><Link to="/travel/trips" className="mt-3 inline-block underline">My trips</Link></div>;
  const city = t.city || {};
  const items = [...(t.items || []), ...(t.suggested || [])];
  return (
    <div>
      <section className="relative overflow-hidden text-white" style={{ background: fieldFor(city.hue) }}>
        {city.photo?.url && <img src={city.photo.url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-black/10" />
        <div className="relative mx-auto max-w-4xl px-4 pb-10 pt-20 sm:px-6">
          <Link to="/travel/trips" className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:underline"><ArrowLeft size={16} /> My trips</Link>
          <h1 className="mt-4 font-serif-display text-5xl font-bold leading-none tracking-tight sm:text-6xl">{t.name || city.name}</h1>
          <p className="mt-3 text-white/90">{prettyDate(t.depart_date)}{t.return_date ? ` – ${prettyDate(t.return_date)}` : ""} · from {t.origin?.label || t.origin?.code} · {t.adults} adult{t.adults > 1 ? "s" : ""}</p>
        </div>
      </section>
      <section className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="trip-flight">
          <h2 className="font-bold">Flight</h2>
          {t.flight ? (
            <p className="mt-2 text-sm">{t.flight.airline} · <span className="font-mono-num font-semibold">{gbp(t.flight.price)}</span>{t.flight.slices?.map((s, i) => <span key={i} className="block text-muted-foreground">{s.origin} → {s.destination} · {(s.departs_at || "").slice(11, 16)}{s.stops ? ` · ${s.stops} stop` : " · direct"}</span>)}</p>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">No flight picked yet.</p>
          )}
          {t.flight_links && (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(t.flight_links).map(([k, l]) => (
                <a key={k} href={l.url} target="_blank" rel={k === "partner" ? "sponsored noopener" : "noopener"} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">{k === "partner" ? `Book on ${l.label || "Aviasales"}` : l.label} <ExternalLink size={14} /></a>
              ))}
            </div>
          )}
        </div>
        <h2 className="mt-10 font-serif-display text-2xl font-bold">Bookings on this trip</h2>
        {items.length ? (
          <ul className="mt-4 space-y-2">
            {items.map((it) => (
              <li key={`${it.kind}-${it.ref_id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm">
                {it.image_url && <img src={it.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{it.title}</span><span className="block text-xs text-muted-foreground">{prettyDate(it.date)}{it.time ? ` · ${it.time}` : ""}{it.distance_km != null ? ` · ${it.distance_km} km from ${city.name}` : ""}</span></span>
                <span className="text-xs capitalize text-muted-foreground">{it.kind}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">Anything you book in {city.name} between those dates shows up here. <Link to="/marketplace" className="underline">Browse the marketplace</Link>.</p>
        )}
        <h2 className="mt-10 font-serif-display text-2xl font-bold">Things to do in {city.name}</h2>
        <div className="mt-4"><ThingsToDo city={city.name} country={city.country} surface="web_trip" /></div>
        <div className="mt-10"><Button variant="ghost" className="text-destructive" onClick={remove}><Trash2 size={16} className="mr-2" /> Delete trip</Button></div>
      </section>
    </div>
  );
}
